import type { Diagram, Relation, ViewState } from "@ae/domain";

/**
 * Herschikken naar een andere container: welke relaties drukte de OUDE
 * nesting uit?
 *
 * Zolang een kind in een container staat, tekent het canvas geen enkele
 * (niet-teken)relatie tussen kind en container — de omhulling ís die relatie
 * (zie `autoHiddenContainmentRelationIds` in Canvas.tsx en `nestOnDiagram`).
 * Sleep je het kind naar container B, dan blijft de relatie naar A in het
 * model staan, onzichtbaar of ineens als losse lijn. Deze helper levert de
 * kandidaten zodat de nest-vraag kan aanbieden ze te verwijderen.
 *
 * Leeg wanneer:
 * - oude en nieuwe container hetzelfde concept zijn (twee instances), of
 * - een ándere instance van het kind op dit diagram nog in (een instance
 *   van) de oude container staat — dan drukt die nesting de relatie nog uit.
 *
 * `viewState` is de stand ná de verplaatsing.
 */
export function relationsRepresentedByOldNesting(args: {
  relations: readonly Relation[];
  viewState: ViewState;
  childConceptId: string;
  oldParentConceptId: string;
  newParentConceptId: string;
}): string[] {
  const { relations, viewState, childConceptId, oldParentConceptId, newParentConceptId } = args;
  if (oldParentConceptId === newParentConceptId) return [];
  if (childConceptId === oldParentConceptId) return [];
  for (const [key, ve] of Object.entries(viewState)) {
    if (ve.position === undefined || !ve.containerId) continue;
    if ((ve.ref ?? key) !== childConceptId) continue;
    const container = viewState[ve.containerId];
    if (container && (container.ref ?? ve.containerId) === oldParentConceptId) return [];
  }
  return relations
    .filter((r) => !r.tombstoneInBranch)
    .filter((r) => !r.typeId.startsWith("drawing."))
    .filter(
      (r) =>
        (r.sourceConceptId === childConceptId && r.targetConceptId === oldParentConceptId) ||
        (r.sourceConceptId === oldParentConceptId && r.targetConceptId === childConceptId),
    )
    .map((r) => r.id);
}

/**
 * Waar is een relatie nog meer te zien? Zelfde regel als het canvas:
 * - heeft de relatie op géén enkel diagram een entry, dan is ze "overal
 *   zichtbaar" (legacy) — op elk diagram waar beide objecten staan;
 * - anders: de diagrammen (behalve `currentDiagramId`) met een niet-verborgen
 *   entry voor de relatie.
 */
export function relationUsageElsewhere(
  relationId: string,
  diagrams: readonly Diagram[],
  currentDiagramId: string,
): { everywhere: true } | { everywhere: false; diagrams: Diagram[] } {
  let placedAnywhere = false;
  const elsewhere: Diagram[] = [];
  for (const d of diagrams) {
    let visibleHere = false;
    for (const [key, ve] of Object.entries(d.viewState)) {
      if (ve.position !== undefined || ve.type === "object") continue;
      if ((ve.ref ?? key) !== relationId) continue;
      placedAnywhere = true;
      if (ve.hidden !== true) visibleHere = true;
    }
    if (visibleHere && d.id !== currentDiagramId) elsewhere.push(d);
  }
  return placedAnywhere ? { everywhere: false, diagrams: elsewhere } : { everywhere: true };
}
